import { useMemo } from 'react';

import { Button, Divider, DownloadIcon, ExcelIcon, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { useStableCallback } from '~/hooks/common';
import { getReceiveStatusColumnDefs } from '~/utils';

import { RECEIVE_PAGE_SIZE } from '~/constants';

import type { TypePagination } from '~/types/common.types';
import type { TypeReceiveStatusRow } from '~/types/receive.types';

import styles from './ReceiveStatusTable.module.scss';

const getReceiveStatusRowId = (row: TypeReceiveStatusRow) => String(row.requestId);

type TypeReceiveStatusTableProps<T extends TypeReceiveStatusRow> = {
  mode?: 'DEFAULT' | '3PL';
  data: T[] | null;
  pagination: TypePagination;
  isDownloadReady: boolean;
  selectedItems: T[];
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (selectedRows: T[]) => void;
  onClickViewDetail: (data: T) => void;
  onClickReceiveStatusExcelDownload: () => void;
  onClickSpecificationDownload: () => void;
};

const ReceiveStatusTable = <T extends TypeReceiveStatusRow>({
  mode = 'DEFAULT',
  data,
  pagination,
  isDownloadReady,
  selectedItems,
  onChangePageLimit,
  onChangePage,
  onChangeRowSelection,
  onClickViewDetail,
  onClickReceiveStatusExcelDownload,
  onClickSpecificationDownload,
}: TypeReceiveStatusTableProps<T>) => {
  // 컬럼은 TypeReceiveStatusRow 로 그린다. 표가 돌려주는 행은 data 의 원소라, 호출부 타입(T)의 행은 data 에서 다시 찾는다.
  // 부모 핸들러가 렌더마다 새 함수여도 컬럼(셀 함수)을 다시 만들지 않는다.
  const handleClickViewDetail = useStableCallback((row: TypeReceiveStatusRow) => {
    const target = data?.find((item) => item === row);
    if (target) onClickViewDetail(target);
  });
  const { columnDefs } = useMemo(
    () => getReceiveStatusColumnDefs({ mode, onClickViewDetail: handleClickViewDetail }),
    [mode, handleClickViewDetail]
  );

  const selectedRowIds = useMemo(() => selectedItems.map(getReceiveStatusRowId), [selectedItems]);

  const handleChangeRowSelection = (rows: TypeReceiveStatusRow[]) => {
    const selectedRows = new Set(rows);
    onChangeRowSelection((data ?? []).filter((row) => selectedRows.has(row)));
  };

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <div className={styles['table-top-right-field']}>
            <Button
              size="tiny"
              variant="outlined"
              color="tertiary"
              leftIcon={<DownloadIcon size={16} />}
              disabled={selectedItems.length === 0}
              onClick={onClickSpecificationDownload}
            >
              서명확인용 거래명세서
            </Button>
            <Divider className={styles['divider']} orientation="vertical" color="gray100" />
            <Button
              variant="outlined"
              color="tertiary"
              size="tiny"
              disabled={!isDownloadReady}
              leftIcon={<ExcelIcon size={16} />}
              onClick={onClickReceiveStatusExcelDownload}
            >
              엑셀 전체 다운로드
            </Button>
          </div>
        }
      />
      <Table<TypeReceiveStatusRow>
        className={styles['table']}
        data={data}
        columns={columnDefs}
        rowStatusType={data === null ? 'INIT' : 'EMPTY'}
        getRowId={getReceiveStatusRowId}
        selection={{ selectedRowIds, onChange: handleChangeRowSelection }}
      />
      <TableBottomField
        pageSizeOptions={Object.values(RECEIVE_PAGE_SIZE)}
        currentPage={pagination.currentPage}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={onChangePage}
      />
    </div>
  );
};

export default ReceiveStatusTable;
